import { apiClient } from './client'
import { ApiError } from './errors'
import type { Job, JobList, JobState } from '@/types/domain'

const http = () => apiClient.axios

/**
 * The analysis jobs of one template.
 *
 * `GET /v1/templates/:id/jobs` is the admin job listing scoped to the
 * template in the path, so it answers the admin envelope
 * (`{jobs, total, limit, offset, has_more}`) and its wire names are
 * snake_case throughout. Starting a job is `documentsApi.startAnalysis`;
 * this module only watches and stops them.
 */

/** Terminal states. A job in one of these will not change again. */
const FINISHED: JobState[] = ['completed', 'failed', 'cancelled']

/** Still doing something, so the cockpit keeps polling and shows
 *  "Analyse läuft". */
export function isJobActive(job: Job): boolean {
  return job.status === 'pending' || job.status === 'processing'
}

export function isJobFinished(job: Job): boolean {
  return FINISHED.includes(job.status)
}

/**
 * Whether a job belongs in the cockpit's "Problemfälle".
 *
 * Only `failed` does. `cancelled` is terminal and carries an `error` of
 * "Cancelled" — the gateway sets it so the reason is visible — but nothing
 * went wrong: somebody pressed stop. Counting it as a failure would report a
 * problem for every deliberate cancellation, which is exactly the noise that
 * makes a problem list stop being read.
 */
export function isJobFailure(job: Job): boolean {
  return job.status === 'failed'
}

/** The messages for the two refusals cancelling produces. The gateway sends
 *  the queue's own English error text on the 400, which is not something to
 *  put in front of a person. */
function mapCancelError(error: unknown): ApiError {
  const api = ApiError.from(error)
  if (api.isNotFound) {
    return new ApiError({
      status: api.status,
      code: 'job_not_found',
      message: 'Der Auftrag wurde nicht gefunden.',
      details: api.details,
      cause: api.cause,
    })
  }
  if (api.status === 400) {
    return new ApiError({
      status: 400,
      code: 'job_not_cancellable',
      message: 'Der Auftrag ist bereits beendet und kann nicht mehr abgebrochen werden.',
      details: api.details,
      cause: api.cause,
    })
  }
  return api
}

export const jobsApi = {
  /**
   * Lists the template's jobs, newest first.
   *
   * The gateway's own defaults are `limit` 50 (capped at 1000) and
   * `created_at desc`; they are not repeated here, so a caller that passes
   * nothing gets whatever the gateway considers sensible rather than a
   * second, drifting copy of the same numbers.
   */
  async list(
    templateId: string,
    options: {
      status?: JobState
      limit?: number
      offset?: number
      sortBy?: 'created_at' | 'updated_at'
      sortOrder?: 'asc' | 'desc'
    } = {},
  ): Promise<JobList> {
    const { data } = await http().get<Partial<JobList>>(`/v1/templates/${templateId}/jobs`, {
      params: {
        ...(options.status ? { status: options.status } : {}),
        ...(options.limit != null ? { limit: options.limit } : {}),
        ...(options.offset != null ? { offset: options.offset } : {}),
        ...(options.sortBy ? { sort_by: options.sortBy } : {}),
        ...(options.sortOrder ? { sort_order: options.sortOrder } : {}),
      },
    })
    const jobs = data.jobs ?? []
    return {
      jobs,
      total: data.total ?? jobs.length,
      limit: data.limit ?? jobs.length,
      offset: data.offset ?? 0,
      has_more: data.has_more ?? false,
    }
  },

  /**
   * Stops a pending or processing job and returns it in its new state.
   *
   * The stop is not immediate: the gateway only flips the status, and the
   * worker notices when it next checks — on pick-up and between documents —
   * so it finishes what is already running and puts the documents it never
   * reached back to `neu`. A job that has already finished is a 400.
   */
  async cancel(templateId: string, jobId: string): Promise<Job | null> {
    try {
      const { data } = await http().post<{ message?: string; job?: Job }>(
        `/v1/templates/${templateId}/jobs/${jobId}/cancel`,
      )
      return data.job ?? null
    } catch (error) {
      throw mapCancelError(error)
    }
  },
}
